import { useState } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Icon } from '@/components/ui/Icon/Icon';
import { ApiRequestError } from '@/services/api';
import { useCreateKategori, useDeleteKategori, useRenameKategori } from '@/services/kategorier';
import { nb } from '@/i18n/nb';
import type { VedlikeholdKategori } from '@/types/api';
import s from './EntryFormModal.module.css';
import k from './KategoriManagementModal.module.css';

/*
 * Manage Generelt vedlikehold kategorier (per kategori-management.png):
 *   - Add a new kategori (registered_guest+ can add)
 *   - Rename an existing kategori (owner+)
 *   - Delete an empty kategori (owner+) — disabled with tooltip when entriesCount > 0;
 *     server-side 409 surfaced as a banner if the UI state is stale
 *
 * Role gating in the UI follows the kategori's `canRename` / `canDelete` fields
 * (server-derived from session). The server's VedlikeholdKategoriService is the
 * authoritative check.
 */
export function KategoriManagementModal({
  kategorier,
  onClose,
}: {
  kategorier: VedlikeholdKategori[];
  onClose: () => void;
}) {
  const [newName, setNewName] = useState<string>('');
  const [newError, setNewError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);

  const createMutation = useCreateKategori();

  function handleCreate() {
    setNewError(null);
    const name = newName.trim();
    if (name.length === 0) {
      setNewError(nb.vedlikehold.addModal.validation.kategoriNameRequired);
      return;
    }
    if (name.length > 64) {
      setNewError(nb.vedlikehold.addModal.validation.kategoriNameMax);
      return;
    }
    createMutation.mutate(name, {
      onSuccess: () => setNewName(''),
      onError: (e) =>
        setNewError(e instanceof ApiRequestError ? e.message : nb.vedlikehold.addModal.errorTitle),
    });
  }

  return (
    <Modal open={true} onClose={onClose} title={nb.vedlikehold.kategoriManagement.title}>
      <div className={k.body}>
        {conflict && (
          <p role="alert" className={s.submitError}>
            {conflict}
          </p>
        )}

        <div className={k.addRow}>
          <input
            type="text"
            className={s.input}
            placeholder={nb.vedlikehold.addModal.newKategoriPlaceholder}
            value={newName}
            maxLength={64}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreate();
              }
            }}
            aria-label={nb.vedlikehold.kategoriManagement.add}
          />
          <Button variant="primary" loading={createMutation.isPending} onClick={handleCreate}>
            <Icon name="plus" size={16} /> {nb.vedlikehold.kategoriManagement.addSubmit}
          </Button>
        </div>
        {newError && (
          <span role="alert" className={s.errorMsg}>
            {newError}
          </span>
        )}

        {kategorier.length === 0 ? (
          <p className={k.empty}>{nb.vedlikehold.kategoriManagement.empty}</p>
        ) : (
          <ul className={k.list}>
            {kategorier.map((kategori) => (
              <KategoriRow key={kategori.id} kategori={kategori} onConflict={setConflict} />
            ))}
          </ul>
        )}

        <div className={s.actions}>
          <Button variant="secondary" onClick={onClose}>
            {nb.common.close}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function KategoriRow({
  kategori,
  onConflict,
}: {
  kategori: VedlikeholdKategori;
  onConflict: (message: string | null) => void;
}) {
  const [editing, setEditing] = useState<boolean>(false);
  const [name, setName] = useState<string>(kategori.name);
  const [rowError, setRowError] = useState<string | null>(null);

  const renameMutation = useRenameKategori(kategori.id);
  const deleteMutation = useDeleteKategori();

  const canDelete = kategori.canDelete && kategori.entriesCount === 0;
  const deleteTooltip =
    kategori.entriesCount > 0 ? nb.vedlikehold.kategoriManagement.deleteDisabledTooltip : undefined;

  function handleRename() {
    setRowError(null);
    const next = name.trim();
    if (next.length === 0 || next === kategori.name) {
      setEditing(false);
      return;
    }
    renameMutation.mutate(next, {
      onSuccess: () => setEditing(false),
      onError: (e) => setRowError(e instanceof ApiRequestError ? e.message : null),
    });
  }

  function handleDelete() {
    onConflict(null);
    deleteMutation.mutate(kategori.id, {
      onError: (e) => {
        if (e instanceof ApiRequestError && e.status === 409) {
          onConflict(nb.vedlikehold.kategoriManagement.conflict);
        } else if (e instanceof ApiRequestError) {
          onConflict(e.message);
        }
      },
    });
  }

  return (
    <li className={k.row}>
      {editing ? (
        <input
          type="text"
          className={s.input}
          value={name}
          maxLength={64}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleRename();
            }
            if (e.key === 'Escape') {
              setEditing(false);
              setName(kategori.name);
            }
          }}
          aria-label={`${nb.vedlikehold.kategoriManagement.renameLabel}: ${kategori.name}`}
          autoFocus
        />
      ) : (
        <span className={k.name}>
          {kategori.name} <span className={k.count}>({kategori.entriesCount})</span>
        </span>
      )}
      <div className={k.rowActions}>
        {editing ? (
          <>
            <Button variant="primary" loading={renameMutation.isPending} onClick={handleRename}>
              {nb.documents.editor.save}
            </Button>
            <button
              type="button"
              className={s.linkButton}
              onClick={() => {
                setEditing(false);
                setName(kategori.name);
              }}
            >
              {nb.common.cancel}
            </button>
          </>
        ) : (
          <>
            {kategori.canRename && (
              <button type="button" className={k.actionBtn} onClick={() => setEditing(true)}>
                <Icon name="pencil" size={14} />
              </button>
            )}
            {kategori.canDelete && (
              <button
                type="button"
                className={k.actionBtn}
                data-variant="danger"
                onClick={handleDelete}
                disabled={!canDelete || deleteMutation.isPending}
                title={deleteTooltip}
                aria-label={`${nb.vedlikehold.kategoriManagement.delete} ${kategori.name}`}
              >
                <Icon name="trash" size={14} />
              </button>
            )}
          </>
        )}
      </div>
      {rowError && (
        <span role="alert" className={s.errorMsg}>
          {rowError}
        </span>
      )}
    </li>
  );
}
